import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { CheckCircle2, XCircle, AlertTriangle, ExternalLink, Trash2, Plug } from 'lucide-react';
import {
  fetchCatalogue,
  saveConnection,
  deleteConnection,
  testConnection,
  oauthStartUrl,
} from '@/lib/integrationsClient';
import {
  CATEGORY_LABELS,
  CATEGORY_ORDER,
  type Catalogue,
  type Connection,
  type Provider,
  type TestResult,
} from '@/features/integrations/types';
import { getToken } from '@/lib/auth';
import { ApiError } from '@/lib/api';

// The Research tab IS the Sensarra "Research connections" surface, copied
// verbatim from the standalone site (perfect UI/UX as-is per Director direction,
// 2026-07-04). It replaced the earlier Stage-1 bake-off workbench. Data flows
// through the Seitiate+ backend integrations proxy
// (`/api/v1/research/integrations/*`), which forwards to the managed research
// worker with the caller's `X-Research-User`.

// Every Seitiate+ caller reaching this tab is already authenticated (AppShell
// gates it); these adapters mirror the standalone site's auth helpers.
function isAuthenticated(): boolean {
  return Boolean(getToken());
}
function redirectToLogin(): void {
  if (typeof window !== 'undefined') window.location.assign('/login');
}

interface ConnectionGroup {
  id: string;
  title: string;
  intent: string;
  categories: string[];
  workflows: string;
}

const CONNECTION_GROUPS: ConnectionGroup[] = [
  {
    id: 'literature-context',
    title: 'Literature context',
    intent: 'Find, cite, and sanity-check prior work around a run.',
    categories: ['literature', 'reference_manager'],
    workflows: 'Literature tab, report notes, supervisor context',
  },
  {
    id: 'molecular-context',
    title: 'Molecular and assay context',
    intent: 'Pull target, compound, protein, and model metadata into evidence capsules.',
    categories: ['chemistry_biology', 'models'],
    workflows: 'Molecular tab, aptamer exploration, live model assist',
  },
  {
    id: 'identity-output',
    title: 'Identity and outputs',
    intent: 'Connect researcher identity and repositories for provenance and exports.',
    categories: ['identity', 'data_repository'],
    workflows: 'Sealed reports, future receipt/export flows',
  },
  {
    id: 'agents-ip-custom',
    title: 'Research agents, IP, and custom tools',
    intent: 'Attach specialist agents, patent sources, and lab-specific MCP tools.',
    categories: ['research_agent', 'patents', 'mcp'],
    workflows: 'Advanced discovery, IP checks, custom lab connectors',
  },
];

const CATEGORY_COPY: Record<string, string> = {
  identity: 'Researcher identity and attribution surfaces used by provenance and future sign-off flows.',
  literature: 'Search and citation sources for context, not benchmark pass/fail decisions.',
  reference_manager: 'Personal libraries and citation managers that support report preparation.',
  research_agent: 'External research assistants and paid agents; connect only accounts the researcher owns.',
  data_repository: 'Repositories for sealed artifacts, data deposits, and export destinations.',
  patents: 'Patent and IP discovery sources for novelty and prior-art context.',
  chemistry_biology: 'Public molecular, protein, chemistry, and enzyme databases for target context.',
  models: 'Model and dataset providers; outputs remain claim-limited and metered where applicable.',
  mcp: 'Advanced custom connectors for lab-specific or institution-specific tools.',
};

const CATEGORY_SINGULAR_LABELS: Record<string, string> = {
  identity: 'Researcher identity',
  reference_manager: 'Reference manager',
  research_agent: 'Research agent',
  data_repository: 'Data or output repository',
  patents: 'Patent and IP source',
  chemistry_biology: 'Chemistry or biology data source',
  models: 'Model provider',
  mcp: 'Custom MCP connector',
};

/**
 * Research connections: scholars connect their own research tools (scite MCP,
 * Zotero, PubMed, custom MCP servers, …) by supplying credentials. The catalogue
 * is registry-driven by the worker, so this page renders whatever providers the
 * active deployment surface exposes.
 */
export function ResearchPage() {
  const [catalogue, setCatalogue] = useState<Catalogue | null>(null);
  const [error, setError] = useState<{ message: string; gated: boolean } | null>(null);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    try {
      setCatalogue(await fetchCatalogue());
      setError(null);
    } catch (err) {
      // 503 = hosted worker not configured on this deployment yet
      // (RESEARCH_WORKER_URL unset). Fail closed with a friendly message.
      const gated = err instanceof ApiError && err.status === 503;
      setError({ message: err instanceof Error ? err.message : String(err), gated });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
  }, []);

  const grouped = useMemo(() => {
    const groups = new Map<string, Provider[]>();
    for (const provider of catalogue?.providers ?? []) {
      const list = groups.get(provider.category) ?? [];
      list.push(provider);
      groups.set(provider.category, list);
    }
    return [...groups.entries()].sort(
      ([a], [b]) => indexOrLast(a) - indexOrLast(b),
    );
  }, [catalogue]);

  const groupCards = useMemo(() => {
    if (!catalogue) return [];
    return CONNECTION_GROUPS.map((group) => {
      const providers = catalogue.providers.filter((provider) => group.categories.includes(provider.category));
      return {
        ...group,
        providers,
        connected: providers.reduce((sum, provider) => sum + connectedCount(provider), 0),
        total: providers.reduce((sum, provider) => sum + providerSlotCount(provider), 0),
      };
    }).filter((group) => group.providers.length > 0);
  }, [catalogue]);

  // Surface the result of an OAuth round-trip (the worker redirects back here
  // with ?connected=… or ?error=…).
  const oauthBanner = useMemo(() => readOAuthBanner(), []);

  return (
    <div style={{ maxWidth: 1080, margin: '0 auto' }}>
      <div style={{ marginBottom: 'var(--sp-space-5)' }}>
        <h1 style={{ fontSize: 24, margin: '0 0 6px', display: 'flex', alignItems: 'center', gap: 10, fontWeight: 400 }}>
          <Plug size={20} /> Research connections
        </h1>
        <p style={{ color: 'var(--sp-text-muted)', margin: 0, maxWidth: 640 }}>
          Connect external scientific tools with your own credentials. Start with the purpose cards, then expand the
          specific provider group you need.
        </p>
        {catalogue && (
          <span
            className="mono"
            style={{
              display: 'inline-block',
              marginTop: 10,
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              color: 'var(--sp-text-dim)',
            }}
          >
            {catalogue.deploy_mode === 'hosted' ? 'Hosted platform' : 'Local IDE build'} ·{' '}
            {catalogue.providers.length} available
          </span>
        )}
      </div>

      {oauthBanner && (
        <div
          style={{
            border: `1px solid ${oauthBanner.ok ? 'var(--sp-ok)' : 'var(--sp-error)'}`,
            color: oauthBanner.ok ? 'var(--sp-ok)' : 'var(--sp-error)',
            borderRadius: 'var(--sp-radius)',
            padding: 'var(--sp-space-3)',
            marginBottom: 'var(--sp-space-4)',
            fontSize: 13,
          }}
        >
          {oauthBanner.text}
        </div>
      )}

      {loading && <p style={{ color: 'var(--sp-text-muted)' }}>Loading connections…</p>}
      {error && (
        <div
          style={{
            border: `1px solid ${error.gated ? 'var(--sp-border-strong)' : 'var(--sp-error)'}`,
            borderRadius: 'var(--sp-radius)',
            color: error.gated ? 'var(--sp-text-muted)' : 'var(--sp-error)',
            padding: 'var(--sp-space-3)',
            marginBottom: 'var(--sp-space-4)',
          }}
        >
          {error.gated
            ? 'Connection settings are temporarily unavailable because the hosted worker is not fully configured.'
            : `Could not load connections: ${error.message}`}
        </div>
      )}

      {catalogue && (
        <>
          <section style={{ marginBottom: 'var(--sp-space-6)' }}>
            <div className="mono" style={sectionEyebrowStyle}>Connection map</div>
            <div style={groupGridStyle}>
              {groupCards.map((group) => (
                <a key={group.id} href={`#connections-${group.categories[0]}`} style={groupCardStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                    <strong style={{ color: 'var(--sp-text)', fontSize: 15 }}>{group.title}</strong>
                    <span className="mono" style={{ color: 'var(--sp-text-dim)', fontSize: 10, whiteSpace: 'nowrap' }}>
                      {group.connected}/{group.total}
                    </span>
                  </div>
                  <p style={{ color: 'var(--sp-text-muted)', fontSize: 12, lineHeight: 1.5, margin: '8px 0' }}>
                    {group.intent}
                  </p>
                  <div style={{ color: 'var(--sp-text-dim)', fontSize: 11 }}>{group.workflows}</div>
                </a>
              ))}
            </div>
          </section>

          <section>
            <div className="mono" style={sectionEyebrowStyle}>Manage providers</div>
            <div style={{ display: 'grid', gap: 'var(--sp-space-3)' }}>
              {grouped.map(([category, providers]) => {
                const connected = providers.reduce((sum, provider) => sum + connectedCount(provider), 0);
                const total = providers.reduce((sum, provider) => sum + providerSlotCount(provider), 0);
                return (
                  <details key={category} id={`connections-${category}`} style={categoryDetailsStyle} open={connected > 0}>
                    <summary style={categorySummaryStyle}>
                      <span>
                        <span style={{ display: 'block', color: 'var(--sp-text)', fontWeight: 700 }}>
                          {categoryLabel(category, providers.length)}
                        </span>
                        <span style={{ display: 'block', color: 'var(--sp-text-muted)', fontSize: 12, lineHeight: 1.45 }}>
                          {CATEGORY_COPY[category] ?? 'Connection providers for this research workflow.'}
                        </span>
                      </span>
                      <span className="mono" style={{ color: 'var(--sp-text-dim)', fontSize: 11, whiteSpace: 'nowrap' }}>
                        {connected}/{total} connected
                      </span>
                    </summary>
                    <div style={providerGridStyle}>
                      {providers.map((provider) => (
                        provider.instances ? (
                          <RepeatableProviderCard key={provider.id} provider={provider} onChanged={reload} />
                        ) : (
                          <ProviderCard key={provider.id} provider={provider} onChanged={reload} />
                        )
                      ))}
                    </div>
                  </details>
                );
              })}
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function RepeatableProviderCard({ provider, onChanged }: { provider: Provider; onChanged: () => void }) {
  const instances = provider.connection_instances ?? [];
  const [adding, setAdding] = useState(instances.length === 0);

  const onInstanceChanged = () => {
    setAdding(false);
    onChanged();
  };

  return (
    <>
      {instances.map((connection) => (
        <ProviderCard
          key={connection.provider_id}
          provider={providerForConnection(provider, connection)}
          onChanged={onChanged}
        />
      ))}
      {adding ? (
        <ProviderCard provider={{ ...provider, connection: null, connection_instances: [] }} onChanged={onInstanceChanged} />
      ) : (
        <button type="button" className="sp-btn" style={addAnotherStyle} onClick={() => setAdding(true)}>
          Add another {repeatableNoun(provider)}
        </button>
      )}
    </>
  );
}

function ProviderCard({ provider, onChanged }: { provider: Provider; onChanged: () => void }) {
  const [values, setValues] = useState<Record<string, string>>(() => initialValues(provider));
  const [busy, setBusy] = useState<null | 'save' | 'test' | 'delete'>(null);
  const [test, setTest] = useState<TestResult | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const connected = provider.connection != null;

  const run = async (kind: 'save' | 'test' | 'delete', fn: () => Promise<void>) => {
    setBusy(kind);
    setMessage(null);
    try {
      await fn();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setMessage(`Error: ${msg}`);
    } finally {
      setBusy(null);
    }
  };

  const requireSignedIn = () => {
    setMessage('Sign in to connect or change this provider.');
    redirectToLogin();
  };

  const runWhenSignedIn = (kind: 'save' | 'test' | 'delete', fn: () => Promise<void>) => {
    if (!isAuthenticated()) {
      requireSignedIn();
      return;
    }
    void run(kind, fn);
  };

  const onSave = () =>
    runWhenSignedIn('save', async () => {
      await saveConnection(provider.id, values, true, provider.instances && !connected);
      setMessage('Saved.');
      setValues(initialValues(provider, true));
      onChanged();
    });

  const onTest = () =>
    runWhenSignedIn('test', async () => {
      setTest(await testConnection(provider.id));
    });

  const onDelete = () =>
    runWhenSignedIn('delete', async () => {
      await deleteConnection(provider.id);
      setTest(null);
      setMessage('Removed.');
      onChanged();
    });

  return (
    <div
      style={{
        border: '1px solid var(--sp-border)',
        borderRadius: 'var(--sp-radius-lg)',
        background: 'var(--sp-bg-elev)',
        padding: 'var(--sp-space-4)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <strong style={{ fontSize: 15 }}>{provider.name}</strong>
            <ConnStatus connected={connected} />
            {provider.availability === 'ide_only' && <Badge label="local only" tone="warn" />}
            {provider.tags.includes('billed') && <Badge label="billed" tone="warn" />}
            {provider.tags.includes('free') && <Badge label="free" tone="ok" />}
            {provider.auth_type === 'mcp' && <Badge label="MCP" tone="muted" />}
          </div>
          <p style={{ color: 'var(--sp-text-muted)', margin: '6px 0 0', fontSize: 13 }}>
            {provider.summary}
          </p>
        </div>
        <a
          href={provider.docs_url}
          target="_blank"
          rel="noreferrer"
          className="mono"
          style={{ fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}
        >
          docs <ExternalLink size={12} />
        </a>
      </div>

      {provider.auth_type === 'oauth' ? (
        <OAuthBody provider={provider} connected={connected} busy={busy} onDelete={onDelete} message={message} />
      ) : (
        <>
          {provider.fields.length === 0 ? (
            <p style={{ fontSize: 12, color: 'var(--sp-text-dim)', marginTop: 'var(--sp-space-3)' }}>
              No credentials required — enable to use this source.
            </p>
          ) : (
            <div style={{ display: 'grid', gap: 'var(--sp-space-3)', marginTop: 'var(--sp-space-4)' }}>
              {provider.fields.map((field) => {
                const secretState = provider.connection?.secrets[field.key];
                const placeholder = secretState?.configured
                  ? `•••• ${secretState.last4} — leave blank to keep`
                  : field.placeholder;
                return (
                  <div key={field.key}>
                    <label className="sp-label">
                      {field.label}
                      {field.required ? '' : ' (optional)'}
                    </label>
                    <input
                      className="sp-input"
                      type={field.secret ? 'password' : 'text'}
                      value={values[field.key] ?? ''}
                      placeholder={placeholder}
                      autoComplete="off"
                      onChange={(e) => setValues((v) => ({ ...v, [field.key]: e.target.value }))}
                    />
                    {field.help && (
                      <div style={{ fontSize: 11, color: 'var(--sp-text-dim)', marginTop: 4 }}>{field.help}</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 'var(--sp-space-4)', flexWrap: 'wrap' }}>
            <button type="button" className="sp-btn sp-btn-primary" disabled={busy != null} onClick={onSave}>
              {busy === 'save' ? 'Saving…' : connected ? 'Update' : provider.fields.length === 0 ? 'Enable' : 'Connect'}
            </button>
            {provider.test_supported && connected && (
              <button type="button" className="sp-btn" disabled={busy != null} onClick={onTest}>
                {busy === 'test' ? 'Testing…' : 'Test connection'}
              </button>
            )}
            {connected && (
              <button
                type="button"
                className="sp-btn"
                disabled={busy != null}
                onClick={onDelete}
                style={{ color: 'var(--sp-error)', borderColor: 'var(--sp-error)' }}
              >
                <Trash2 size={13} style={{ marginRight: 4, verticalAlign: '-2px' }} />
                Remove
              </button>
            )}
            {message && <span style={{ fontSize: 12, color: 'var(--sp-text-muted)' }}>{message}</span>}
            {test && <TestPill result={test} />}
          </div>
        </>
      )}
    </div>
  );
}

function OAuthBody({
  provider,
  connected,
  busy,
  onDelete,
  message,
}: {
  provider: Provider;
  connected: boolean;
  busy: null | 'save' | 'test' | 'delete';
  onDelete: () => void;
  message: string | null;
}) {
  const configured = provider.oauth_configured ?? false;
  const start = () => {
    if (!isAuthenticated()) {
      redirectToLogin();
      return;
    }
    window.location.href = oauthStartUrl(provider.id);
  };
  const identity = provider.connection?.fields ?? {};
  return (
    <div style={{ marginTop: 'var(--sp-space-4)', display: 'flex', flexDirection: 'column', gap: 10 }}>
      {connected ? (
        <>
          <div style={{ fontSize: 13 }}>
            {Object.entries(identity).map(([k, v]) => (
              <div key={k}>
                <span className="mono" style={{ color: 'var(--sp-text-dim)' }}>{k}:</span> {v}
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <button type="button" className="sp-btn" disabled={!configured} onClick={start}>
              Reconnect
            </button>
            <button
              type="button"
              className="sp-btn"
              disabled={busy != null}
              onClick={onDelete}
              style={{ color: 'var(--sp-error)', borderColor: 'var(--sp-error)' }}
            >
              <Trash2 size={13} style={{ marginRight: 4, verticalAlign: '-2px' }} />
              Disconnect
            </button>
            {message && <span style={{ fontSize: 12, color: 'var(--sp-text-muted)' }}>{message}</span>}
          </div>
        </>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button type="button" className="sp-btn sp-btn-primary" disabled={!configured} onClick={start}>
            Connect with {provider.name}
          </button>
          {!configured && (
            <span style={{ fontSize: 12, color: 'var(--sp-text-dim)' }}>
              OAuth not configured by the operator yet.
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function indexOrLast(category: string): number {
  const i = CATEGORY_ORDER.indexOf(category);
  return i === -1 ? CATEGORY_ORDER.length : i;
}

function connectedCount(provider: Provider): number {
  if (provider.instances) {
    return (provider.connection_instances ?? []).filter((connection) => connection.enabled).length;
  }
  return provider.connection?.enabled ? 1 : 0;
}

function providerSlotCount(provider: Provider): number {
  if (provider.instances) {
    return Math.max(1, provider.connection_instances?.length ?? 0);
  }
  return 1;
}

function categoryLabel(category: string, providerCount: number): string {
  if (providerCount === 1 && CATEGORY_SINGULAR_LABELS[category]) {
    return CATEGORY_SINGULAR_LABELS[category];
  }
  return CATEGORY_LABELS[category] ?? category;
}

function providerForConnection(provider: Provider, connection: Connection): Provider {
  const label = connection.fields.label?.trim();
  return {
    ...provider,
    id: connection.provider_id,
    name: label ? `${provider.name}: ${label}` : provider.name,
    connection,
    connection_instances: [],
  };
}

function repeatableNoun(provider: Provider): string {
  return provider.id === 'custom_mcp' ? 'MCP server' : 'connection';
}

function readOAuthBanner(): { ok: boolean; text: string } | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const connected = params.get('connected');
  const error = params.get('error');
  if (connected) return { ok: true, text: `Connected ${connected} successfully.` };
  if (error) return { ok: false, text: `Could not connect: ${error}` };
  return null;
}

function initialValues(provider: Provider, reset = false): Record<string, string> {
  const out: Record<string, string> = {};
  for (const field of provider.fields) {
    if (field.secret) {
      out[field.key] = '';
    } else {
      out[field.key] = (!reset && provider.connection?.fields[field.key]) || field.default || '';
    }
  }
  return out;
}

function ConnStatus({ connected }: { connected: boolean }) {
  return connected ? (
    <span style={{ color: 'var(--sp-ok)', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <CheckCircle2 size={13} /> Connected
    </span>
  ) : (
    <span style={{ color: 'var(--sp-text-dim)', fontSize: 12 }}>Not connected</span>
  );
}

function TestPill({ result }: { result: TestResult }) {
  const tone = result.ok === true ? 'var(--sp-ok)' : result.ok === false ? 'var(--sp-error)' : 'var(--sp-warn)';
  const Icon = result.ok === true ? CheckCircle2 : result.ok === false ? XCircle : AlertTriangle;
  return (
    <span
      className="mono"
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: tone, fontSize: 11 }}
    >
      <Icon size={13} /> {result.detail}
    </span>
  );
}

function Badge({ label, tone }: { label: string; tone: 'warn' | 'ok' | 'muted' }) {
  const color =
    tone === 'warn' ? 'var(--sp-warn)' : tone === 'ok' ? 'var(--sp-ok)' : 'var(--sp-text-dim)';
  return (
    <span
      className="mono"
      style={{
        border: `1px solid ${color}`,
        color,
        borderRadius: 'var(--sp-radius)',
        padding: '1px 6px',
        fontSize: 10,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
      }}
    >
      {label}
    </span>
  );
}

const sectionEyebrowStyle = {
  color: 'var(--sp-text-dim)',
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  marginBottom: 'var(--sp-space-3)',
} satisfies CSSProperties;

const groupGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: 'var(--sp-space-3)',
} satisfies CSSProperties;

const groupCardStyle = {
  display: 'block',
  textDecoration: 'none',
  border: '1px solid var(--sp-border)',
  borderRadius: 'var(--sp-radius-lg)',
  background: 'var(--sp-bg-elev)',
  padding: 'var(--sp-space-4)',
} satisfies CSSProperties;

const categoryDetailsStyle = {
  border: '1px solid var(--sp-border)',
  borderRadius: 'var(--sp-radius-lg)',
  background: 'var(--sp-bg)',
  overflow: 'hidden',
} satisfies CSSProperties;

const categorySummaryStyle = {
  cursor: 'pointer',
  display: 'flex',
  justifyContent: 'space-between',
  gap: 16,
  alignItems: 'flex-start',
  padding: 'var(--sp-space-4)',
  listStyle: 'none',
} satisfies CSSProperties;

const providerGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  gap: 'var(--sp-space-3)',
  padding: '0 var(--sp-space-4) var(--sp-space-4)',
} satisfies CSSProperties;

const addAnotherStyle = {
  minHeight: 74,
  borderStyle: 'dashed',
  justifyContent: 'center',
} satisfies CSSProperties;
